import { type FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "@/lib/apiClient";
import { dueDateFromTerms, INVOICE_PAYMENT_TERMS } from "@/lib/financeInvoiceLineMath";
import { useFinanceInvoicePaths } from "@/lib/financeRouteContext";
import type { FinanceCustomer, FinanceInvoice } from "@/lib/financeTypes";
import { FinanceErrorBanner } from "@/lib/financeUi";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";

type CustomersResponse = { customers?: FinanceCustomer[] };

type DraftLine = {
  description: string;
  quantity: string;
  unitPrice: string;
};


function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function lineAmount(line: DraftLine): number {
  const q = Number(line.quantity) || 0;
  const u = Number(line.unitPrice) || 0;
  return Math.round(q * u * 100) / 100;
}

export function FinanceInvoiceNewPage() {
  const navigate = useNavigate();
  const { listPath, detailPath, accountsPath } = useFinanceInvoicePaths();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState<FinanceCustomer[]>([]);
  const [customerId, setCustomerId] = useState("");
  const [issueDate, setIssueDate] = useState(todayIsoDate);
  const [paymentTerms, setPaymentTerms] = useState("net_15");
  const [dueDate, setDueDate] = useState(() => dueDateFromTerms(todayIsoDate(), "net_15"));
  const [dueDateManual, setDueDateManual] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");
  const [subject, setSubject] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([
    { description: "", quantity: "1", unitPrice: "0" },
  ]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await apiGet<CustomersResponse>("/api/finance/customers?limit=200");
      if (cancelled || !res.ok) return;
      setCustomers(res.data?.customers ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (dueDateManual || paymentTerms === "custom") return;
    const next = dueDateFromTerms(issueDate, paymentTerms);
    if (next) setDueDate(next);
  }, [issueDate, paymentTerms, dueDateManual]);

  const total = lines.reduce((sum, line) => sum + lineAmount(line), 0);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!customerId) {
      setError("Select an account.");
      return;
    }
    const apiLines = lines
      .filter((l) => l.description.trim())
      .map((l) => {
        const quantity = Number(l.quantity) || 0;
        const unitPrice = Number(l.unitPrice) || 0;
        const amount = lineAmount(l);
        return { description: l.description.trim(), quantity, unitPrice, amount };
      });
    if (apiLines.length === 0) {
      setError("Add at least one line with a description.");
      return;
    }
    setSaving(true);
    setError(null);
    const res = await apiPost<FinanceInvoice>("/api/finance/invoices", {
      customerId,
      status: "draft",
      amount: total,
      issueDate: issueDate || null,
      paymentTerms: paymentTerms || null,
      dueDate: dueDate || null,
      orderNumber: orderNumber.trim() || null,
      subject: subject.trim() || null,
      customerNotes: customerNotes.trim() || null,
      termsAndConditions: termsAndConditions.trim() || null,
      lines: apiLines,
    });
    setSaving(false);
    if (!res.ok || !res.data?.id) {
      setError(res.error || "Could not create invoice.");
      return;
    }
    void navigate(detailPath(res.data.id));
  };

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <Link
        to={listPath}
        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All invoices
      </Link>

      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">New invoice</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Draft invoice with line items. Customer (account) is required.
        </p>
      </div>

      {error ? <FinanceErrorBanner message={error} /> : null}

      <form onSubmit={(e) => void onSubmit(e)} className="space-y-6">
        <div className="max-w-xl rounded-lg border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-slate-950/40">
          <label className="block text-sm">
            <span className="font-medium text-slate-700 dark:text-slate-300">Account *</span>
            <select
              required
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
            >
              <option value="">Select account…</option>
              {customers.map((c) =>
                c.id ? (
                  <option key={c.id} value={c.id}>
                    {c.name?.trim() || c.id}
                  </option>
                ) : null,
              )}
            </select>
          </label>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Issue date</span>
              <input
                type="date"
                value={issueDate}
                onChange={(e) => setIssueDate(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Payment terms</span>
              <select
                value={paymentTerms}
                onChange={(e) => {
                  setPaymentTerms(e.target.value);
                  setDueDateManual(e.target.value === "custom");
                }}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              >
                {INVOICE_PAYMENT_TERMS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Due date</span>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => {
                  setDueDate(e.target.value);
                  setDueDateManual(true);
                  if (paymentTerms !== "custom") setPaymentTerms("custom");
                }}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-300">Order number</span>
              <input
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
            <label className="block text-sm sm:col-span-2">
              <span className="font-medium text-slate-700 dark:text-slate-300">Subject</span>
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
              />
            </label>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Need a new account?{" "}
            <Link to={accountsPath} className="text-indigo-600 hover:underline dark:text-indigo-300">
              Add one in Sales → Accounts
            </Link>
            , or{" "}
            <Link to="/finance/customers/new" className="text-indigo-600 hover:underline dark:text-indigo-300">
              create via legacy path
            </Link>
            .
          </p>
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950/40">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-white/10">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Line items</h2>
            <button
              type="button"
              onClick={() => setLines((prev) => [...prev, { description: "", quantity: "1", unitPrice: "0" }])}
              className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 dark:text-indigo-300"
            >
              <Plus className="h-4 w-4" aria-hidden />
              Add line
            </button>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-white/5">
            {lines.map((line, index) => (
              <div key={index} className="grid gap-3 p-4 sm:grid-cols-12 sm:items-end">
                <label className="block text-sm sm:col-span-5">
                  <span className="font-medium text-slate-700 dark:text-slate-300">Description</span>
                  <input
                    value={line.description}
                    onChange={(e) =>
                      setLines((prev) => prev.map((l, i) => (i === index ? { ...l, description: e.target.value } : l)))
                    }
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                  />
                </label>
                <label className="block text-sm sm:col-span-2">
                  <span className="font-medium text-slate-700 dark:text-slate-300">Qty</span>
                  <input
                    type="number"
                    min={0}
                    step="1"
                    value={line.quantity}
                    onChange={(e) =>
                      setLines((prev) => prev.map((l, i) => (i === index ? { ...l, quantity: e.target.value } : l)))
                    }
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                  />
                </label>
                <label className="block text-sm sm:col-span-2">
                  <span className="font-medium text-slate-700 dark:text-slate-300">Unit price</span>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={line.unitPrice}
                    onChange={(e) =>
                      setLines((prev) => prev.map((l, i) => (i === index ? { ...l, unitPrice: e.target.value } : l)))
                    }
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
                  />
                </label>
                <div className="text-sm sm:col-span-2">
                  <span className="font-medium text-slate-700 dark:text-slate-300">Line total</span>
                  <p className="mt-2 tabular-nums font-medium text-slate-900 dark:text-white">
                    ${lineAmount(line).toFixed(2)}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={lines.length <= 1}
                  onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                  className="inline-flex items-center justify-center rounded-md border border-slate-200 p-2 text-slate-500 hover:text-rose-600 disabled:opacity-30 dark:border-white/10 sm:col-span-1"
                  aria-label="Remove line"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200 px-4 py-3 text-right text-sm font-semibold dark:border-white/10">
            Draft total: ${total.toFixed(2)}
          </div>
        </div>

        <div className="grid gap-4 rounded-lg border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-slate-950/40 sm:grid-cols-2">
          <label className="block text-sm sm:col-span-2">
            <span className="font-medium text-slate-700 dark:text-slate-300">Account notes</span>
            <textarea
              value={customerNotes}
              onChange={(e) => setCustomerNotes(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
            />
          </label>
          <label className="block text-sm sm:col-span-2">
            <span className="font-medium text-slate-700 dark:text-slate-300">Terms and conditions</span>
            <textarea
              value={termsAndConditions}
              onChange={(e) => setTermsAndConditions(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
            />
          </label>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {saving ? "Creating…" : "Create draft invoice"}
        </button>
      </form>
    </div>
  );
}
